<template>
    <div>
        <base-checkbox class="mb-3" v-model="showSelection">
            {{ $t("customize-cases") }}
        </base-checkbox>

        <div v-if="showSelection" class="case-list ml-4">
            <div v-for="(caseItem, index) in casesInCurrentLocale" :key="index" class="mb-2">
                <div class="d-flex align-items-start">
                    <base-checkbox 
                        v-model="selectedCases[index]" 
                        :disabled="disableDeselection(index)"
                        class="me-2"
                    />
                    <div>
                        <span class="small" @click="toggleDetails(index)" style="cursor: pointer;">
                            {{ caseItem.case_number }}. {{ caseItem.title }}
                            <span v-if="expandedCases[index]">▼</span>
                            <span v-else>▶</span>
                        </span>
                        <div v-if="expandedCases[index]" class="case-details mt-2">
                            <p class="small"><strong>{{ $t("description") }}:</strong> {{ caseItem.description }}</p>
                            <p class="small"><strong>{{ $t("question") }}:</strong> {{ caseItem.case_question }}</p>
                            <div class="small">
                                <strong>{{ $t("answering-options") }}:</strong>
                                <ul>
                                    <li v-for="option in getCaseChoices(caseItem)" :key="option.id">
                                        {{ option.choice_text }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getRPSCases, getRPSCaseChoices } from "@/services/APIService";

export default {
    name: "CaseSelector",
    props: {
        scenarioId: {
            required: true
        }
    },
    data() {
        return {
            cases: {},
            selectedCases: [],
            expandedCases: [],
            caseChoices: {},
            showSelection: false
        };
    },
    computed: {
        casesInCurrentLocale() {
            return Object.values(this.cases)
                .filter(c => c.locale === this.$i18n.locale)
                .sort((a, b) => a.case_number - b.case_number);
        },
        selectedCaseNumbers() {
            if (!this.showSelection) {
                return null;
            }
            return this.casesInCurrentLocale
                .filter((_, index) => this.selectedCases[index])
                .map(caseItem => caseItem.case_number)
                .sort((a, b) => a - b)
                .join(',');
        },
        selectedCount() {
            return this.selectedCases.filter(selected => selected).length;
        }
    },
    methods: {
        async loadCases() {
            try {
                const cases = await getRPSCases(this.scenarioId);
                this.cases = cases.data || cases;
                
                const choices = await getRPSCaseChoices(this.scenarioId);
                this.caseChoices = choices.data || choices;
                
                this.selectedCases = new Array(this.casesInCurrentLocale.length).fill(true);
                this.expandedCases = new Array(this.casesInCurrentLocale.length).fill(false);
            } catch (error) {
                console.error("Error loading cases:", error);
            }
        },
        toggleDetails(index) {
            this.$set(this.expandedCases, index, !this.expandedCases[index]);
        },
        getCaseChoices(caseItem) {
            if (!this.caseChoices || !Array.isArray(this.caseChoices)) {
                return [];
            }
            return this.caseChoices
                .filter(choice => 
                    choice.rps_case === caseItem.rps_case && 
                    choice.locale === this.$i18n.locale)
                .sort((a, b) => a.order_number - b.order_number);
        },
        disableDeselection(index) {
            return this.selectedCases[index] && this.selectedCount <= 1;
        }
    },
    watch: {
        scenarioId: {
            immediate: true,
            async handler(newId) {
                if (newId) {
                    await this.loadCases();
                }
            }
        }
    }
};
</script>

<i18n>
{
  "en": {
    "customize-cases": "Select specific cases to be used in the simulation - enables focusing on desired topics or shorter game time",
    "description": "Description",
    "question": "Question",
    "answering-options": "Answering Options"
  },
  "fi": {
    "customize-cases": "Valitse simulaatiossa käytettävät tilanteet - mahdollistaa keskittymisen haluttuihin aiheisiin tai lyhyemmän peliajan",
    "description": "Kuvaus",
    "question": "Kysymys",
    "answering-options": "Vastausvaihtoehdot"
  },
  "sv": {
    "customize-cases": "Välj specifika fall som ska användas i simuleringen - möjliggör fokus på önskade ämnen eller kortare speltid",
    "description": "Beskrivning",
    "question": "Fråga",
    "answering-options": "Svarsalternativ"
  },
  "de": {
    "customize-cases": "Wählen Sie spezifische Fälle für die Simulation aus - ermöglicht die Konzentration auf gewünschte Themen oder kürzere Spielzeit",
    "description": "Beschreibung",
    "question": "Frage",
    "answering-options": "Antwortmöglichkeiten"
  },
  "ko": {
    "customize-cases": "시뮬레이션에서 사용할 특정 사례 선택 - 원하는 주제에 집중하거나 게임 시간을 단축할 수 있습니다",
    "description": "설명",
    "question": "질문",
    "answering-options": "답변 옵션"
  }
}
</i18n>
